import RootState from '@/app/appStore';
import { Dispatch } from 'react';
import { updateCell } from '../row/slice';
import { makeSelectNonGroupColumns, makeSelectTableStateByTableId } from "../selectors";
import { Cell, Column, Row, SummaryColumn, TableState, ValueType } from "../types";
import TableStyle, { ROW_HEIGHT, SCROLLBAR_WIDTH } from "./constants";
import { calculateAllColumnRatios, calculateColumnWidth, calculateHeaderHeight, calculateMinWidth, calculateScrollValues, crispPixel, findColumnById, getMaxDepth, getVerticalScrollbarInfo, isAutoFitApplicable } from "./helper";
import { setEditingCell } from './slice';


/**
 * 모든 행의 선택 상태를 초기화하는 함수
 * 
 * @param rows - 초기화할 행 배열
 * @returns Row[] - 선택 상태가 초기화된 새로운 행 배열
 */
export const resetRowsSelection = (rows: Row[]): Row[] => {
    return rows.map(row => ({
        ...row,
        selected: false,
        data: Object.keys(row.data).reduce((acc, key) => {
            acc[key] = { ...row.data[key], selected: false };
            return acc;
        }, {} as { [key: string]: Cell })
    }));
};

/**
 * 단일 행의 선택 상태를 초기화하는 함수
 * 
 * @param row - 초기화할 행 객체
 * @returns Row - 선택 상태가 초기화된 새로운 행 객체
 */
export const resetRowSelection = (row: Row): Row => {
    return {
        ...row,
        selected: false,
        data: Object.keys(row.data).reduce((acc, key) => {
            acc[key] = {
                ...row.data[key],
                selected: false
            };
            return acc;
        }, {} as { [key: string]: Cell })
    };
};



/**
 * 테이블의 스크롤 위치를 조정하는 함수
 * 
 * @param rowIndex - 대상 행 인덱스
 * @param columnIndex - 대상 열 인덱스
 * @param tableId - 테이블 식별자
 * @param canvasRef - Canvas 요소에 대한 참조
 * @returns {{ newScrollY: number, newScrollX: number }} 새로운 스크롤 위치
 */
export const adjustScroll = (
    rowIndex: number,
    columnIndex: number,
    tableId: string,
    canvasRef: React.RefObject<HTMLCanvasElement>,
) => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    const nonGroupColumns = makeSelectNonGroupColumns(tableId)(RootState.getState()).filter(column => column.visible !== false);

    if (!canvasRef.current) return { newScrollY: tableState.scrollY, newScrollX: tableState.scrollX };

    let newScrollY = tableState.scrollY;
    let newScrollX = tableState.scrollX;

    // 헤더 높이 계산
    const headerHeight = calculateHeaderHeight(tableState.columns);

    // 캔버스 높이에서 헤더와 스크롤바, 요약 행을 제외한 실제 테이블 영역 높이 계산
    const canvasHeight = canvasRef.current.clientHeight;
    const tableHeight = canvasHeight - headerHeight - SCROLLBAR_WIDTH - (tableState.hasSummaryColumn ? ROW_HEIGHT : 0);

    // 보이는 행 계산
    const visibleStartRowIndex = Math.floor(tableState.scrollY / ROW_HEIGHT);
    const visibleRowCount = Math.floor(tableHeight / ROW_HEIGHT);
    const visibleEndRowIndex = visibleStartRowIndex + visibleRowCount - 1;

    // 대상 컬럼의 시작 위치와 끝 위치 계산
    const targetColumnStartPosition = nonGroupColumns.slice(0, columnIndex).reduce((acc, column) => acc + column.width, 0);
    const targetColumnEndPosition = nonGroupColumns.slice(0, columnIndex + 1).reduce((acc, column) => acc + column.width, 0);
    const availableWidth = canvasRef.current.clientWidth - SCROLLBAR_WIDTH;

    // 수직 스크롤 조정
    if (rowIndex < visibleStartRowIndex) {
        // 위로 스크롤이 필요한 경우
        newScrollY = rowIndex * ROW_HEIGHT;
    } else if (rowIndex > visibleEndRowIndex) {
        // 아래로 스크롤이 필요한 경우
        newScrollY = (rowIndex - visibleRowCount + 1) * ROW_HEIGHT;
    }

    // 수평 스크롤 조정
    if (targetColumnStartPosition < tableState.scrollX) {
        // 왼쪽으로 스크롤이 필요한 경우
        newScrollX = targetColumnStartPosition;
    } else if (targetColumnEndPosition > tableState.scrollX + availableWidth) {
        // 오른쪽으로 스크롤이 필요한 경우
        newScrollX = targetColumnEndPosition - availableWidth;
    }

    // 스크롤 범위 제한
    newScrollY = Math.max(0, Math.min(newScrollY, tableState.maxScrollY));
    newScrollX = Math.max(0, Math.min(newScrollX, tableState.maxScrollX));

    return { newScrollY, newScrollX };
};



/**
 * Canvas에 테이블 셀을 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 셀의 X 좌표
 * @param y - 셀의 Y 좌표
 * @param width - 셀의 너비
 * @param height - 셀의 높이
 * @param text - 셀에 표시할 텍스트
 * @param color - 텍스트 색상
 * 
 * @description
 * 1. 셀의 테두리를 검은색으로 그림
 * 2. 지정된 색상으로 텍스트를 셀 내부에 렌더링
 * 3. 텍스트는 셀의 왼쪽에서 10px 떨어진 위치에서 시작하며, 수직 중앙에 정렬됨
 */
export const drawCell = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    text: string,
    color: string
) => {
    ctx.strokeStyle = '#000';
    ctx.strokeRect(x, y, width, height);
    ctx.fillStyle = color;
    ctx.fillText(text, x + 10, y + height / 2);
};


/**
 * 그룹 컬럼의 너비를 조정하고 자식 컬럼들의 너비를 비례적으로 조정하는 함수
 * 
 * @param groupColumn - 조정할 그룹 컬럼
 * @param newWidth - 새로운 전체 너비
 * @param columns - 전체 컬럼 맵
 * @returns 조정된 최종 너비
 * 
 * 동작 방식:
 * 1. 그룹 컬럼이 아니거나 자식이 없는 경우 newWidth 반환
 * 2. 현재 전체 너비와 새로운 너비의 차이 계산
 * 3. 자식 컬럼들의 너비를 비례적으로 조정
 * 4. 최소 너비(10px) 제한 적용
 */
export const adjustGroupColumnWidth = (
    groupColumn: Column,
    newWidth: number,
    columns: { [columnId: string]: Column }
): number => {
    if (!groupColumn.children || groupColumn.type !== ValueType.GROUP) {
        return newWidth;
    }

    const currentTotalWidth = calculateColumnWidth(groupColumn, columns);
    const widthDiff = newWidth - currentTotalWidth;

    // 모든 자식 컬럼의 현재 너비 합계 계산
    const totalChildrenWidth = groupColumn.children.reduce(
        (sum, child) => sum + columns[child.id].width,
        0
    );

    // 각 자식 컬럼의 너비를 비례적으로 조정
    groupColumn.children.forEach(child => {
        const ratio = columns[child.id].width / totalChildrenWidth;
        const adjustedWidth = columns[child.id].width + (widthDiff * ratio);
        columns[child.id].width = Math.max(10, adjustedWidth);
    });

    // 조정된 실제 너비 반환
    return groupColumn.children.reduce(
        (sum, child) => sum + columns[child.id].width,
        0
    );
};



/**
 * 사각형을 그리는 함수 (라인만 그리고 배경은 그리지 않음 배경은 drawBackground 함수로 참고)
 * @param ctx Canvas 렌더링 컨텍스트
 * @param x X 좌표
 * @param y Y 좌표
 * @param width 너비
 * @param height 높이
 * @param strokeStyle 테두리 스타일
 * @param lineWidth 선 두께
 */
export const drawRectangle = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    strokeStyle: string,
    lineWidth: number = 1
) => {
    ctx.save();

    // 픽셀 정렬 적용
    const alignedX = crispPixel(x, lineWidth);
    const alignedY = crispPixel(y, lineWidth);
    const alignedWidth = crispPixel(width + x, lineWidth) - alignedX;
    const alignedHeight = crispPixel(height + y, lineWidth) - alignedY;

    ctx.strokeStyle = strokeStyle;
    ctx.lineWidth = lineWidth;
    ctx.strokeRect(alignedX, alignedY, alignedWidth, alignedHeight);
    ctx.restore();
};



/**
 * Canvas에 텍스트를 렌더링하는 유틸리티 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param text - 렌더링할 텍스트 문자열
 * @param x - 텍스트를 그릴 X 좌표
 * @param y - 텍스트를 그릴 Y 좌표
 * @param font - 텍스트의 폰트 스타일 (예: "12px Arial")
 * @param color - 텍스트 색상 (예: "#000000")
 * @param textAlign - 텍스트 정렬 방식 (기본값: "left")
 * @param textBaseline - 텍스트 기준선 위치 (기본값: "middle")
 * @param maxWidth - 텍스트의 최대 너비 (선택 사항)
 * 
 * @example
 * drawText(ctx, "Hello", 10, 20, "12px Arial", "#000000", "center", "middle");
 */
export const drawText = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    font: string,
    color: string,
    textAlign: CanvasTextAlign = "center",
    textBaseline: CanvasTextBaseline = "middle",
    maxWidth?: number
) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = font;
    ctx.textAlign = textAlign;
    ctx.textBaseline = textBaseline;

    // 줄바꿈 문자로 텍스트를 분할
    const lines = text.split('\n');

    // 폰트 크기 계산 (예: "12px Arial"에서 12를 추출)
    const fontSize = parseInt(font.match(/\d+/)?.[0] || '12', 10);
    const lineHeight = fontSize * 0.14;

    // 각 줄을 그림
    lines.forEach((line, index) => {
        const lineY = y + (index - (lines.length - 1) / 2) * lineHeight;
        ctx.fillText(line, x, lineY, maxWidth);
    });

    ctx.restore();
};

/**
 * Canvas에 ellipsis가 적용된 텍스트를 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param text - 그릴 텍스트
 * @param x - 텍스트의 X 좌표
 * @param y - 텍스트의 Y 좌표
 * @param font - 폰트 스타일
 * @param color - 텍스트 색상
 * @param textAlign - 텍스트 정렬 방식
 * @param textBaseline - 텍스트 기준선
 * @param maxWidth - 최대 너비 (이 너비를 초과하면 ellipsis 적용)
 * 
 * @description
 * maxWidth를 초과하는 텍스트는 말줄임표(...)를 추가하여 표시합니다.
 */
export const drawTextWithEllipsis = (
    ctx: CanvasRenderingContext2D,
    text: string,
    x: number,
    y: number,
    font: string,
    color: string,
    textAlign: CanvasTextAlign = "center",
    textBaseline: CanvasTextBaseline = "middle",
    maxWidth?: number
) => {
    ctx.save();
    ctx.fillStyle = color;
    ctx.font = font;
    ctx.textAlign = textAlign;
    ctx.textBaseline = textBaseline;

    if (!maxWidth) {
        // maxWidth가 없으면 기본 drawText와 동일하게 처리
        ctx.fillText(text, x, y);
        ctx.restore();
        return;
    }

    // 줄바꿈 문자로 텍스트를 분할
    const lines = text.split('\n');

    // 폰트 크기 계산 (예: "12px Arial"에서 12를 추출)
    const fontSize = parseInt(font.match(/\d+/)?.[0] || '12', 10);
    const lineHeight = fontSize * 0.14;

    // 각 줄을 그림
    lines.forEach((line, index) => {
        const lineY = y + (index - (lines.length - 1) / 2) * lineHeight;

        // 텍스트 너비 측정
        const textWidth = ctx.measureText(line).width;

        if (textWidth <= maxWidth) {
            // 텍스트가 maxWidth 이하이면 그대로 그리기
            ctx.fillText(line, x, lineY);
        } else {
            // 텍스트가 maxWidth를 초과하면 ellipsis 처리
            const ellipsis = '...';
            const ellipsisWidth = ctx.measureText(ellipsis).width;

            // ellipsis를 제외한 사용 가능한 너비
            const availableWidth = maxWidth - ellipsisWidth;

            // 이진 탐색으로 잘라낼 텍스트 길이 찾기
            let left = 0;
            let right = line.length;
            let bestLength = 0;

            while (left <= right) {
                const mid = Math.floor((left + right) / 2);
                const truncatedText = line.substring(0, mid);
                const truncatedWidth = ctx.measureText(truncatedText).width;

                if (truncatedWidth <= availableWidth) {
                    bestLength = mid;
                    left = mid + 1;
                } else {
                    right = mid - 1;
                }
            }

            // 잘라낸 텍스트 + ellipsis 그리기
            const finalText = line.substring(0, bestLength) + ellipsis;
            ctx.fillText(finalText, x, lineY);
        }
    });

    ctx.restore();
};



/**
 * Canvas에 체크박스를 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 체크박스의 X 좌표
 * @param y - 체크박스의 Y 좌표
 * @param size - 체크박스의 크기 (정사각형)
 * @param isChecked - 체크박스의 선택 상태
 * 
 * @description
 * 1. 체크박스 배경을 그립니다
 * 2. 체크박스 테두리를 그립니다
 * 3. 선택된 상태인 경우 체크마크를 그립니다
 */
export const drawCheckbox = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    isChecked: boolean
) => {
    // 체크박스 배경
    drawBackground(ctx, x, y, size, size, TableStyle.CHECKBOX_FILL);
    // 체크박스 테두리
    drawBorderLine(ctx, x, y, size, size, 1, TableStyle.CHECKBOX_STROKE);

    if (isChecked) {
        ctx.strokeStyle = TableStyle.CHECKMARK;
        ctx.beginPath();
        ctx.moveTo(crispPixel(x + 2), crispPixel(y + 6));
        ctx.lineTo(crispPixel(x + 5), crispPixel(y + 10));
        ctx.lineTo(crispPixel(x + 11), crispPixel(y + 3));
        ctx.stroke();
    }
};



/**
 * Canvas에 테두리 선을 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 테두리를 그릴 시작점의 X 좌표
 * @param y - 테두리를 그릴 시작점의 Y 좌표
 * @param width - 테두리의 너비
 * @param height - 테두리의 높이
 * @param lineWidth - 테두리 선의 두께
 * @param color - 테두리 선의 색상
 * @param alpha - 테두리 선의 투명도 (0~1, 기본값: 1)
 * 
 * @description
 * 1. 선명한 테두리를 위해 픽셀 정렬을 적용합니다
 * 2. 테두리 스타일(색상, 두께, 투명도)을 설정합니다
 * 3. 지정된 크기와 위치에 테두리를 그립니다
 * 
 * @example
 * drawBorderLine(ctx, 10, 10, 100, 50, 1, "#000000", 0.5);
 */
export const drawBorderLine = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    lineWidth: number,
    color: string,
    alpha?: number
) => {
    ctx.save();
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.globalAlpha = alpha === undefined ? 1 : alpha;

    // 픽셀 정렬 적용
    const alignedX = crispPixel(x, lineWidth);
    const alignedY = crispPixel(y, lineWidth);
    const alignedWidth = crispPixel(width + x, lineWidth) - alignedX;
    const alignedHeight = crispPixel(height + y, lineWidth) - alignedY;

    ctx.strokeRect(alignedX, alignedY, alignedWidth, alignedHeight);
    ctx.restore();
};



/**
 * Canvas에 배경을 그리는 함수
 * 
 * @param ctx - Canvas 렌더링 컨텍스트
 * @param x - 배경을 그릴 시작점의 X 좌표
 * @param y - 배경을 그릴 시작점의 Y 좌표
 * @param width - 배경의 너비
 * @param height - 배경의 높이
 * @param fillStyle - 배경 색상
 * @param alpha - 배경의 투명도 (0~1, 기본값: 1)
 * 
 * @description
 * 1. 선명한 배경을 위해 픽셀 정렬을 적용합니다
 * 2. 배경 스타일(색상, 투명도)을 설정합니다
 * 3. 지정된 크기와 위치에 배경을 그립니다
 * 
 * @example
 * drawBackground(ctx, 10, 10, 100, 50, "#ffffff", 0.5);
 */
export const drawBackground = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    fillStyle: string,
    alpha?: number
) => {
    ctx.save();
    ctx.fillStyle = fillStyle;
    ctx.globalAlpha = alpha === undefined ? 1 : alpha;

    // 픽셀 정렬 적용
    const alignedX = crispPixel(x);
    const alignedY = crispPixel(y);
    const alignedWidth = crispPixel(width + x) - alignedX;
    const alignedHeight = crispPixel(height + y) - alignedY;

    ctx.fillRect(alignedX, alignedY, alignedWidth, alignedHeight);
    ctx.restore();
};




export const drawLine = (
    ctx: CanvasRenderingContext2D,
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    color: string,
    lineWidth: number
) => {
    ctx.strokeStyle = color;
    ctx.lineWidth = lineWidth;
    ctx.beginPath();
    // 픽셀 정렬 적용
    const alignedX1 = crispPixel(x1, lineWidth);
    const alignedY1 = crispPixel(y1, lineWidth);
    const alignedX2 = crispPixel(x2, lineWidth);
    const alignedY2 = crispPixel(y2, lineWidth);
    ctx.moveTo(alignedX1, alignedY1);
    ctx.lineTo(alignedX2, alignedY2);
    ctx.stroke();
}





/**
 * 특정 컬럼의 너비를 적용하는 함수
 */
export const applyColumnWidth = (column: Column, newWidth: number) => {
    const MIN_WIDTH = 30.0;
    column.width = Math.max(newWidth, MIN_WIDTH);

    // GROUP 컬럼인 경우 자식들에게 autoFit 적용 (부모 너비를 꽉 채움)
    if (column.type === ValueType.GROUP && column.children?.length) {
        const children = column.children;
        const parentWidth = column.width;
        
        // 모든 자식의 ratio 합계 계산
        const totalRatio = children.reduce((sum, child) => sum + (child.ratio || 0), 0);
        
        if (totalRatio > 0) {
            // ratio에 따라 부모 너비를 꽉 채우도록 분배
            let allocatedWidth = 0;
            
            children.forEach((child, index) => {
                if (index === children.length - 1) {
                    // 마지막 자식은 남은 공간 전체를 할당하여 정확히 부모 너비에 맞춤
                    child.width = Math.max(parentWidth - allocatedWidth, MIN_WIDTH);
                } else {
                    // 비례적으로 분배
                    const proportionalWidth = (parentWidth * (child.ratio || 0)) / totalRatio;
                    child.width = Math.max(proportionalWidth, MIN_WIDTH);
                    allocatedWidth += child.width;
                }
                
                // 자식이 GROUP인 경우 재귀적으로 조정
                if (child.type === ValueType.GROUP && child.children?.length) {
                    applyColumnWidth(child, child.width);
                }
            });
        }

        // 자식들의 ratio 업데이트 (부모 너비 대비 상대적인 비율)
        if (parentWidth > 0) {
            children.forEach(child => {
                child.ratio = child.width / parentWidth;
            });
        }
    }
};


/**
 * AutoFit을 적용하는 함수 (ratio 기준으로 width 재계산)
 */
export const applyAutoFit = (table: TableState) => {
    if (!table.autoFit || table.canvasWidth <= 0) return;

    // 최상위 컬럼들만 처리
    const topLevelColumns = Object.values(table.columns)
        .filter(col => !col.parent && col.visible !== false);

    if (topLevelColumns.length === 0) return;

    // 세로 스크롤바 계산 로직 재사용
    const { availableWidth } = getVerticalScrollbarInfo(table);

    let iteration = 0;
    const maxIterations = topLevelColumns.length + 2;
    let fixedColumns: Column[] = [];
    let flexibleColumns = [...topLevelColumns];

    while (iteration < maxIterations && flexibleColumns.length > 0) {
        iteration++;

        const fixedWidth = fixedColumns.reduce((sum, col) => sum + col.width, 0);
        const remainingWidth = availableWidth - fixedWidth;

        if (remainingWidth <= 0) break;

        const flexibleRatioSum = flexibleColumns.reduce((sum, col) => sum + (col.ratio || 0), 0);

        if (flexibleRatioSum <= 0) {
            // ratio가 없으면 균등 분배
            const equalWidth = remainingWidth / flexibleColumns.length;
            let accumulatedWidth = 0;

            flexibleColumns.forEach((col, index) => {
                if (index === flexibleColumns.length - 1) {
                    col.width = Math.max(remainingWidth - accumulatedWidth, calculateMinWidth(col));
                } else {
                    col.width = Math.max(equalWidth, calculateMinWidth(col));
                    accumulatedWidth += col.width;
                }

                if (col.type === ValueType.GROUP && col.children?.length) {
                    applyColumnWidth(col, col.width);
                }
            });
            break;
        }

        // ratio에 따라 계획된 너비 계산
        const plannedWidths = flexibleColumns.map(col => {
            const plannedWidth = remainingWidth * (col.ratio || 0) / flexibleRatioSum;
            const minWidth = calculateMinWidth(col);
            return {
                column: col,
                plannedWidth,
                minWidth,
                needsFixing: plannedWidth < minWidth
            };
        });

        const columnsToFix = plannedWidths.filter(item => item.needsFixing);

        if (columnsToFix.length === 0) {
            // 모든 컬럼이 최소 너비 이상이면 계획된 너비 적용
            let accumulatedWidth = 0;
            plannedWidths.forEach((item, index) => {
                if (index === plannedWidths.length - 1) {
                    const remaining = remainingWidth - accumulatedWidth;
                    item.column.width = Math.max(remaining, item.minWidth);
                } else {
                    item.column.width = item.plannedWidth;
                    accumulatedWidth += item.column.width;
                }

                if (item.column.type === ValueType.GROUP && item.column.children?.length) {
                    applyColumnWidth(item.column, item.column.width);
                }
            });
            break;
        } else {
            // 최소 너비 위반 컬럼들을 고정하고 다시 시도
            columnsToFix.forEach(item => {
                item.column.width = item.minWidth;
                fixedColumns.push(item.column);

                if (item.column.type === ValueType.GROUP && item.column.children?.length) {
                    applyColumnWidth(item.column, item.column.width);
                }
            });

            flexibleColumns = flexibleColumns.filter(col =>
                !columnsToFix.some(item => item.column.id === col.id)
            );
        }
    }

    // 전체 너비 업데이트
    const newTotalWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
    table.contentWidth = newTotalWidth;
};



/**
 * 컨텐츠 너비 업데이트 함수
 */
export const updateContentWidth = (table: TableState) => {
    const topLevelColumns = Object.values(table.columns).filter(col => !col.parent && col.visible !== false);
    table.contentWidth = topLevelColumns.reduce((sum, col) => sum + col.width, 0);
};



/**
 * 부모 컬럼들의 너비만 업데이트하는 함수
 */
export const updateParentColumnsWidth = (table: TableState, columnId: string) => {
    let currentColumn = findColumnById(table, columnId);
    if (!currentColumn) return;

    let parentColumn = currentColumn.parent ? findColumnById(table, currentColumn.parent) : null;
    while (parentColumn) {
        if (parentColumn.type === ValueType.GROUP && parentColumn.children) {
            const oldParentWidth = parentColumn.width;
            // 자식들의 총 너비로 부모 너비 업데이트
            parentColumn.width = parentColumn.children.reduce((sum, child) => sum + child.width, 0);
            
            // 부모 너비가 변경되었으면 자식들의 ratio도 업데이트
            if (oldParentWidth !== parentColumn.width && parentColumn.width > 0) {
                parentColumn.children.forEach(child => {
                    child.ratio = child.width / parentColumn!.width;
                });
            }
        }
        parentColumn = parentColumn.parent ? findColumnById(table, parentColumn.parent) : null;
    }
};


/**
 * 부모 컬럼의 너비를 고정하고 자식 컬럼들 간의 비율을 조정하는 함수
 */
export const adjustSiblingColumnsWithFixedParent = (table: TableState, childColumnId: string, newWidth: number) => {
    const childColumn = findColumnById(table, childColumnId);
    if (!childColumn || !childColumn.parent) return;

    const parentColumn = findColumnById(table, childColumn.parent);
    if (!parentColumn || parentColumn.type !== ValueType.GROUP || !parentColumn.children) return;

    const siblings = parentColumn.children;
    const targetIndex = siblings.findIndex(child => child.id === childColumnId);
    if (targetIndex === -1) return;

    const MIN_WIDTH = 30.0;
    const parentWidth = parentColumn.width;
    
    const requestedWidth = Math.max(newWidth, MIN_WIDTH);
    const currentWidth = childColumn.width;
    const widthDiff = requestedWidth - currentWidth;
    
    const otherSiblingsCurrentWidth = siblings.reduce((sum, sibling, index) => {
        return index !== targetIndex ? sum + sibling.width : sum;
    }, 0);
    
    const otherSiblingsAvailableSpace = siblings.reduce((sum, sibling, index) => {
        return index !== targetIndex ? sum + Math.max(sibling.width - MIN_WIDTH, 0) : sum;
    }, 0);
    
    const actualWidthDiff = widthDiff > 0 ? 
        Math.min(widthDiff, otherSiblingsAvailableSpace) : 
        widthDiff;
    
    const finalWidth = currentWidth + actualWidthDiff;
    childColumn.width = finalWidth;
    
    if (actualWidthDiff > 0) {
        const otherSiblings = siblings.filter((_, index) => index !== targetIndex);
        let remainingDiff = actualWidthDiff;
        const totalAvailableSpace = otherSiblingsAvailableSpace;
        
        otherSiblings.forEach((sibling) => {
            if (totalAvailableSpace > 0 && remainingDiff > 0) {
                const siblingAvailableSpace = Math.max(sibling.width - MIN_WIDTH, 0);
                const reductionRatio = siblingAvailableSpace / totalAvailableSpace;
                const reduction = Math.min(remainingDiff * reductionRatio, siblingAvailableSpace);
                
                sibling.width = Math.max(sibling.width - reduction, MIN_WIDTH);
                remainingDiff -= reduction;
            }
        });
    } else if (actualWidthDiff < 0) {
        const otherSiblings = siblings.filter((_, index) => index !== targetIndex);
        const expandAmount = Math.abs(actualWidthDiff);
        
        if (otherSiblings.length > 0) {
            let allocatedExpansion = 0;
            
            otherSiblings.forEach((sibling, index) => {
                if (index === otherSiblings.length - 1) {
                    sibling.width += expandAmount - allocatedExpansion;
                } else {
                    const expansionRatio = sibling.width / otherSiblingsCurrentWidth;
                    const expansion = expandAmount * expansionRatio;
                    sibling.width += expansion;
                    allocatedExpansion += expansion;
                }
            });
        }
    }
    
    siblings.forEach(sibling => {
        sibling.ratio = sibling.width / parentWidth;
    });
};


/**
 * 컬럼 구조 검증 및 처리 헬퍼 함수
 */
export const validateColumnChildren = (
    column: Column,
    parentId?: string,
    totalDepth?: number,
    currentDepth: number = 1
): Column => {
    // 현재 컬럼의 복사본 생성
    const processedColumn: Column = { ...column };

    // 부모 ID 설정 (자식 컬럼인 경우)
    if (parentId) {
        processedColumn.parent = parentId;
    }

    // GROUP 타입인데 children이 없거나 비어있는 경우 오류 발생
    if (processedColumn.type === ValueType.GROUP && (!processedColumn.children || processedColumn.children.length === 0)) {
        throw new Error(
            `GROUP type column must have children (column id: ${processedColumn.id})`
        );
    }

    // children이 있는 경우 재귀적으로 처리
    if (processedColumn.children) {
        processedColumn.children = processedColumn.children.map(child =>
            validateColumnChildren(child, processedColumn.id, totalDepth, currentDepth + 1)
        );
    }

    // 컬럼 타입 검증
    if (processedColumn.type !== ValueType.GROUP && processedColumn.children && processedColumn.children.length > 0) {
        throw new Error(
            `Non-GROUP column type '${processedColumn.type}' cannot have children (column id: ${processedColumn.id})`
        );
    }

    return processedColumn;
};



/**
 * 컬럼 구조 검증 및 처리 헬퍼 함수
 */
export const processColumnsWithValidation = (columns: Column[]): { [columnId: string]: Column } => {
    const processedColumns = columns.reduce((acc, col) => {
        const processedColumn = validateColumnChildren(col, undefined, getMaxDepth(columns));
        processedColumn.visible = col.visible ?? true;
        acc[col.id] = processedColumn;
        return acc;
    }, {} as { [columnId: string]: Column });

    // 기본 ratio 값 설정
    calculateAllColumnRatios(processedColumns);
    
    return processedColumns;
};


/**
 * 캔버스 크기 변경 시 autoFit 처리를 담당하는 함수
 */
export const handleAutoFitOnCanvasChange = (table: TableState) => {
    if (!table.autoFit) return;

    // 임계점 이하인 경우만 autoFit 적용
    if (isAutoFitApplicable(table)) {
        // 적용 가능: 기존 ratio 유지하면서 캔버스에 맞게 조정
        applyAutoFit(table);
    }
};



/**
 * 컬럼 변경 시 autoFit 처리 헬퍼 함수
 */
export const handleColumnChangeAutoFit = (table: TableState) => {
    // ratio가 설정되지 않은 컬럼이 있는지 확인
    const needsRatioCalculation = Object.values(table.columns).some(col => col.ratio === undefined);
    
    if (needsRatioCalculation) {
        // ratio 계산이 필요한 경우에만 계산
        calculateAllColumnRatios(table.columns);
    }
    
    if (table.autoFit && table.canvasWidth > 0) {
        // autoFit 처리: 임계점을 넘으면 비활성화, 아니면 캔버스에 맞게 조정
        handleAutoFitOnCanvasChange(table);
    } else if (table.autoFit) {
        // 캔버스 너비가 없으면 일단 대기
        table.autoFitPending = true;
    }
};



/**
 * 최상위 컬럼 너비 변경 처리 (AutoFit 상태에서)
 */
export const handleTopLevelColumnWidthChange = (table: TableState, targetColumn: Column, adjustedWidth: number) => {
    // autoFit 상태는 유지하고, ratio만 조정
    const currentRatio = targetColumn.ratio || targetColumn.width;
    const currentWidth = targetColumn.width;
    const widthChangeRatio = adjustedWidth / currentWidth;
    const newRatio = currentRatio * widthChangeRatio;
    
    targetColumn.ratio = newRatio;
    
    // autoFit 적용 가능 여부에 따라 동작 결정
    if (isAutoFitApplicable(table)) {
        applyAutoFit(table);
    } else {
        // 임계점을 넘은 경우: ratio는 유지하되 실제 width는 고정
        applyColumnWidth(targetColumn, adjustedWidth);
        updateContentWidth(table);
    }
};


/**
 * 일반적인 컬럼 너비 변경 처리 (non-autoFit)
 */
export const handleColumnWidthChange = (table: TableState, targetColumn: Column, columnId: string, adjustedWidth: number) => {
    if (targetColumn.parent) {
        // 자식 컬럼의 경우 부모 범위 내에서 형제들과 조정
        adjustSiblingColumnsWithFixedParent(table, columnId, adjustedWidth);
        updateParentColumnsWidth(table, columnId);
    } else {
        // 최상위 컬럼의 경우 직접 조정
        applyColumnWidth(targetColumn, adjustedWidth);
    }
    updateContentWidth(table);
};




/**
 * 컬럼 조작 후 공통 처리 로직
 */
export const handleColumnOperation = (table: TableState, operation: 'add' | 'remove' | 'update', columnData?: any) => {
    switch (operation) {
        case 'add':
            if (columnData) {
                table.columns[columnData.id] = columnData;
            }
            break;
        case 'remove':
            if (columnData) {
                delete table.columns[columnData];
            }
            break;
        case 'update':
            // 업데이트는 이미 처리되었다고 가정
            break;
    }
    
    handleColumnChangeAutoFit(table);
};



/**
 * autoFit 적용 후 스크롤 재계산 공통 처리
 */
export const applyAutoFitAndRecalculateScroll = (table: TableState) => {
    if (table.autoFit && isAutoFitApplicable(table)) {
        applyAutoFit(table);
        calculateScrollValues(table, true);
    }
};

/**
 * 캔버스 크기 변경 시 공통 처리
 */
export const handleCanvasSizeChange = (table: TableState, forceScrollRecalc: boolean) => {
    // 캔버스 너비가 유효한 경우에만 autoFit 처리
    if (table.canvasWidth > 0) {
        handleAutoFitOnCanvasChange(table);
    }
    
    // 스크롤 값 재계산
    calculateScrollValues(table, forceScrollRecalc);
};

/**
 * 콘텐츠 크기 변경 시 공통 처리
 */
export const handleContentSizeChange = (table: TableState, forceScrollRecalc: boolean) => {
    // 스크롤 값 재계산 (콘텐츠 크기는 이미 결정된 상태)
    calculateScrollValues(table, forceScrollRecalc);
};



/**
 * 초기 테이블 상태를 생성하는 헬퍼 함수
 */
export const createInitialTableState = (
    tableId: string,
    processedColumns: { [columnId: string]: Column },
    processedSummaryColumns: { [summarycolumnId: string]: SummaryColumn },
    drawEmptyRows?: boolean,
    fullRowSelect?: boolean,
    rowFilter?: { [key: string]: (row: Row) => Row },
    autoFit?: boolean
): TableState => {
    return {
        tableId,
        columns: processedColumns,
        summaryColumns: processedSummaryColumns,
        selectedCell: { rowIndex: -1, columnIndex: -1 },
        hoveredCell: null,
        selectedColumn: { id: '', grabbed: false },
        hasVerticalScrollbar: false,
        hasHorizontalScrollbar: false,
        scrollX: 0,
        scrollY: 0,
        maxScrollX: 0,
        maxScrollY: 0,
        canvasWidth: 0,
        canvasHeight: 0,
        contentWidth: 0,
        contentHeight: 0,
        editingCell: null,
        drawEmptyRows: drawEmptyRows || false,
        fullRowSelect: fullRowSelect || false,
        rowFilter: rowFilter,
        hasSummaryColumn: Object.keys(processedSummaryColumns).length > 0,
        autoFit: autoFit || false,
        autoFitPending: false,
        dropdownMenu: null,
        isSearchModalOpen: false,
        isEditing: false,
        autoAddRow: false,
        isInitialized: true
    };
};


/**
 * 셀 편집을 종료하는 통합 함수
 * @param tableId - 테이블 ID
 * @param dispatch - Redux dispatch 함수
 * @param isCancelled - 편집이 취소되었는지 여부 (ESC 키)
 * @param onCellValueChange - 셀 값 변경 콜백
 * @returns 값이 실제로 변경되었는지 여부
 */
export const finishCellEditing = (
    tableId: string,
    dispatch: Dispatch<any>,
    isCancelled: boolean = false,
    onCellValueChange?: (rowId: string, columnId: string, value: any) => void | boolean
): boolean => {
    const tableState = makeSelectTableStateByTableId(tableId)(RootState.getState());
    if (!tableState.editingCell) return false;

    const { rowId, columnId, value, originalValue, columnIndex } = tableState.editingCell;
    const hasValueChanged = originalValue !== value;

    // ENUM 타입 컬럼에서 유효성 검증
    const columnsArr = makeSelectNonGroupColumns(tableId)(RootState.getState()).filter(column => column.visible !== false);
    let isValidValue = true;
    
    if (columnIndex >= 0 && columnIndex < columnsArr.length) {
        const currentColumn = columnsArr[columnIndex];
        if (currentColumn?.type === ValueType.ENUM && currentColumn.options) {
            // 빈 값 검증: 옵션에 빈값이 있는 경우만 허용
            if (value === '' || value === null || value === undefined) {
                isValidValue = currentColumn.options.some(option => 
                    option.value === '' || option.value === null || option.value === undefined
                );
            } else {
                // 숫자 값의 경우 정수로 변환해서 비교
                const numValue = parseInt(String(value));
                if (!isNaN(numValue)) {
                    isValidValue = currentColumn.options.some(option => option.value === numValue);
                } else {
                    // 문자열 값의 경우 직접 비교
                    isValidValue = currentColumn.options.some(option => option.value === value);
                }
            }
        }
    }

    // 1. 취소된 경우 또는 ENUM 타입에서 유효하지 않은 값인 경우 원래 값으로 복원하고 종료
    if (isCancelled || !isValidValue) {
        dispatch(setEditingCell({
            tableId,
            editingCell: null
        }));
        dispatch(updateCell({
            tableId,
            rowId,
            columnId,
            cell: { value: originalValue }
        }));
        return false;
    }

    // 2. 값이 변경되었을 때만 콜백 호출 (Redux 업데이트 전에)
    if (hasValueChanged && onCellValueChange) {
        const result = onCellValueChange(rowId, columnId, value);
        if (result === false) {
            // 값 변경 거부 시 원래 값으로 복원하고 편집 상태 종료
            dispatch(setEditingCell({
                tableId,
                editingCell: null
            }));
            dispatch(updateCell({
                tableId,
                rowId,
                columnId,
                cell: { value: originalValue }
            }));
            return false;
        }
    }

    // 3. 편집 상태 종료 (콜백 성공 후)
    dispatch(setEditingCell({
        tableId,
        editingCell: null
    }));

    return hasValueChanged;
};






/**
 * 헤더 클릭 처리 통합 함수
 */
export const handleHeaderClick = (
    clickX: number,
    clickY: number,
    tableId: string,
    tableState: any,
    dispatch: Dispatch<any>
): boolean => {
    const { scrollX } = tableState;
    const visibleColumns = Object.values(tableState.columns).filter((col: any) => col.visible !== false) as Column[];
    
    // 리사이징 영역 클릭인지 확인 - checkColumnResizeArea 사용으로 통합
    const getColumnWidth = (columnId: string) => visibleColumns.find(col => col.id === columnId)?.width || 0;
    
    let isInResize = false;
    let startX = 0;
    for (const column of visibleColumns) {
        if (checkColumnResizeArea(column, clickX, clickY, scrollX, 0, startX, 1, getColumnWidth)) {
            isInResize = true;
            break;
        }
        startX += column.type === ValueType.GROUP && column.children ?
            column.children.reduce((sum, child) => sum + child.width, 0) :
            column.width;
    }
    
    if (isInResize) {
        return false; // 리사이징 영역이므로 정렬 처리하지 않음
    }

    // 클릭된 컬럼 찾기
    const { column: clickedColumn } = findClickedHeaderColumn(
        visibleColumns,
        clickX,
        clickY,
        scrollX
    );

    if (!clickedColumn) {
        return false; // 클릭된 컬럼이 없음
    }

    // 체크박스 전체 체크 기능 처리
    if (clickedColumn.type === ValueType.CHECKBOX) {
        handleHeaderCheckboxClick(tableId, clickedColumn.id, dispatch);
        return true;
    }

    // 그룹 컬럼인 경우 정렬 방지
    if (clickedColumn.type === ValueType.GROUP || 
        (clickedColumn.children && clickedColumn.children.length > 0)) {
        return false;
    }

    // 정렬 처리
    handleColumnSort(tableId, clickedColumn, dispatch);
    return true;
};


/**
 * 헤더 체크박스 클릭 처리
 */
export const handleHeaderCheckboxClick = (
    tableId: string,
    columnId: string,
    dispatch: Dispatch<any>
) => {
    const { areAllCheckboxesChecked, handleBatchCheckboxUpdate } = require('./helper');
    const { makeSelectFilteredRowsByTableId } = require('../selectors');
    
    const rows = makeSelectFilteredRowsByTableId(tableId)(RootState.getState());
    const allChecked = areAllCheckboxesChecked(rows, columnId);
    const newValue = !allChecked;
    
    handleBatchCheckboxUpdate(tableId, columnId, rows, newValue, dispatch);
};


/**
 * 컬럼 정렬 처리
 */
export const handleColumnSort = (
    tableId: string,
    column: Column,
    dispatch: Dispatch<any>
) => {
    const { toggleOrderType } = require('./helper');
    const { clearAllOrderType, setFilter } = require('./slice');
    const { sortRows } = require('../row/slice');
    
    const currentFilter = column.filter;
    const orderType = currentFilter?.orderType;
    const newOrderType = toggleOrderType(orderType);
    const newFilter = { ...currentFilter, orderType: newOrderType };

    dispatch(clearAllOrderType({ tableId }));
    dispatch(setFilter({ tableId, columnId: column.id, filter: newFilter }));
    dispatch(sortRows({ 
        tableId, 
        columnId: column.id, 
        orderType: newOrderType, 
        valueType: column.type 
    }));
};


/**
 * 컬럼 리사이즈 영역 체크 함수 (재귀적)
 */
export const checkColumnResizeArea = (
    column: Column,
    x: number,
    y: number,
    scrollX: number,
    depth: number = 0,
    startX: number = 0,
    parentRowSpan: number = 1,
    getColumnWidth: (id: string) => number
): boolean => {
    const columnWidth = getColumnWidth(column.id);
    const endX = startX + columnWidth;

    const columnY = depth * ROW_HEIGHT;
    const rowSpan = column.rowspan || parentRowSpan;
    const columnHeight = ROW_HEIGHT * rowSpan;

    // 리사이즈 영역 확인 (컬럼의 오른쪽 경계) - 소숫점 정밀도로 부드러운 감지
    const isInResizeArea = Math.abs(x - (endX - scrollX)) < 3.0;

    // 컬럼의 수직 영역 내에 있는지 확인
    // 부모의 rowspan을 고려하여 수직 범위 계산
    const effectiveColumnY = column.type === ValueType.GROUP ? columnY : columnY + ROW_HEIGHT * (parentRowSpan - 1);
    const isInVerticalRange = y >= effectiveColumnY && y <= effectiveColumnY + columnHeight;

    if (isInResizeArea && isInVerticalRange) {
        return true;
    }

    // 자식 컬럼들 확인
    if (column.type === ValueType.GROUP && column.children) {
        let childStartX = startX;
        for (const child of column.children) {
            if (checkColumnResizeArea(child, x, y, scrollX, depth + 1, childStartX, rowSpan, getColumnWidth)) {
                return true;
            }
            childStartX += getColumnWidth(child.id);
        }
    }

    return false;
};


/**
 * 클릭된 헤더 컬럼을 찾는 함수 (재귀적)
 */
export const findClickedHeaderColumn = (
    columns: Column[],
    x: number,
    y: number,
    scrollX: number,
    startX: number = 0,
    depth: number = 0
): { column: Column | null, nextX: number } => {
    let currentX = startX;

    for (const col of columns) {
        if (col.visible === false) continue;

        let colWidth = 0;
        if (col.type === ValueType.GROUP && col.children) {
            colWidth = col.children
                .filter(child => child.visible !== false)
                .reduce((sum, c) => sum + c.width, 0);
        } else {
            colWidth = col.width;
        }

        if (x >= currentX - scrollX && x <= currentX + colWidth - scrollX) {
            // Y 좌표 기반 헤더 계층 판별
            const isGroupHeader = y < (depth + 1) * ROW_HEIGHT;

            // 그룹 헤더 클릭 시 차단
            if (col.type === ValueType.GROUP && isGroupHeader) {
                return { column: null, nextX: currentX + colWidth };
            }

            if (col.type === ValueType.GROUP && col.children) {
                const { column: childCol } = findClickedHeaderColumn(
                    col.children.filter(child => child.visible !== false),
                    x,
                    y,
                    scrollX,
                    currentX,
                    depth + 1 // 깊이 증가
                );
                return { column: childCol, nextX: currentX + colWidth };
            }
            return { column: col, nextX: currentX + colWidth };
        }
        currentX += colWidth;
    }
    return { column: null, nextX: currentX };
};